/**
 * @import { MapProps } from '@vis.gl/react-google-maps'
 * @import { ItineraryDay } from '@/features/itinerary-map/types/itinerary-day'
 */

import { config } from '@/config/config';
import { AttributionControl } from '@/ui/attribution-control/attribution-control';
import { MapTypeControl } from '@/ui/map-type-control/map-type-control';
import { Splitter } from '@/ui/splitter/splitter';
import { createPeakPanelState } from '@/utils/panel';
import { Map } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import { useBounds } from '../../hooks/use-bounds';
import { useDay } from '../../hooks/use-day';
import { useMapDays } from '../../hooks/use-map-days';
import { BackToOverviewControl } from '../back-to-overview-control/back-to-overview-control';
import { DayOverview } from '../day-overview/day-overview';
import { InformationControl } from '../information-control/information-control';
import { ItineraryOverview } from '../itinerary-overview/itinerary-overview';
import { Legend } from '../legend/legend';
import { PathLayer } from '../path-layer';
import { PortsLayer } from '../ports-layer';
import classes from './mobile-map.module.css';

const estimatedCardHeight = 190;
const panelStates = [['100%', '0%'], createPeakPanelState(estimatedCardHeight), ['10%', '90%']];

const peakPanelStateIndex = 1;

/**
 * @param {object} props
 * @param {MapProps} props.mapProps
 * @returns {React.JSX.Element}
 */
export function MobileMap({ mapProps }) {
  const [panelIndex, setPanelIndex] = useState(peakPanelStateIndex);
  const { getCurrentDayNumber, viewDay } = useDay();
  const days = useMapDays();
  useBounds();

  const snapDrawer = (/** @type {React.UIEvent} */ event) => {
    event.preventDefault();
    const nextIndex = Math.max(1, (panelIndex + 1) % panelStates.length);
    setPanelIndex(nextIndex);
  };

  const handleResizeEnd = (/** @type {{ snapIndex: number }} */ event) => {
    if (event.snapIndex >= 0) {
      setPanelIndex(event.snapIndex);
    }
  };

  /** @type {(day: ItineraryDay) => void} */
  const handlePortClick = (day) => {
    viewDay(day.dayNumber);
    setPanelIndex(peakPanelStateIndex);
  };

  return (
    <div className={classes.mobileMap}>
      <Splitter
        gutterSlot={<div style={{ height: '1.2rem' }}></div>}
        panelSizes={panelStates[panelIndex]}
        snapPanelSizes={panelStates}
        onHandleClick={snapDrawer}
        onResizeEnd={handleResizeEnd}
        disableDrag={config.disableDrawerDrag}
      >
        <Map style={{ height: '100%' }} {...mapProps}>
          <PathLayer />
          <PortsLayer days={days} onPortClick={handlePortClick} />
          <BackToOverviewControl />
          <InformationControl />
          <Legend days={days} />
          <AttributionControl />
          <MapTypeControl />
        </Map>
        <div style={{ height: '100%' }}>
          <ItineraryOverview />
          {getCurrentDayNumber() && <DayOverview />}
        </div>
      </Splitter>
    </div>
  );
}
